import type { Locale } from '@multaqa/i18n'
import { formatMoney, useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import type { Condition, ConditionField, MatchMode } from './condition-filter-url'

export function ConditionChips({
  fields,
  conditions,
  match,
  currency,
  locale,
  onRemove,
  onClear,
  onOpen,
  showClear = true,
}: {
  fields: ConditionField[]
  conditions: Condition[]
  match: MatchMode
  currency: string
  locale: Locale
  onRemove: (index: number) => void
  onClear: () => void
  onOpen: (index: number) => void
  showClear?: boolean
}) {
  const { t } = useTranslation('condition-filter')
  if (!conditions.length) return null
  return (
    <fieldset className="flex flex-wrap items-center gap-2 border-0 p-0">
      <legend className="sr-only">{t('applied')}</legend>
      {conditions.map((condition, index) => {
        const field = fields.find((item) => item.key === condition.field)
        const fieldLabel = field
          ? t(`fields.${field.key}`, { defaultValue: field.key })
          : condition.field
        const valueText = readableValue(condition, field, currency, locale, t)
        return (
          <span
            key={`${condition.field}-${condition.op}-${JSON.stringify(condition.value)}`}
            className="inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-muted/50 py-1 ps-3 pe-1 text-small"
          >
            <button
              type="button"
              className="max-w-[min(70vw,32rem)] truncate text-start focus-visible:outline-2 focus-visible:outline-ring"
              onClick={() => onOpen(index)}
            >
              {condition.op === 'last_n_days'
                ? t('chipLastDays', { field: fieldLabel, count: condition.value })
                : t('chip', {
                    field: fieldLabel,
                    operator: t(`operators.${condition.op}`),
                    value: valueText,
                  })}
            </button>
            <button
              type="button"
              aria-label={t('removeCondition', { condition: fieldLabel })}
              className="size-7 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
              onClick={() => onRemove(index)}
            >
              ×
            </button>
          </span>
        )
      })}
      <span className="sr-only">{t(`matchMode.${match}`)}</span>
      {showClear ? (
        <Button type="button" variant="ghost" size="sm" onClick={onClear}>
          {t('clearAll')}
        </Button>
      ) : null}
    </fieldset>
  )
}

function readableValue(
  condition: Condition,
  field: ConditionField | undefined,
  currency: string,
  locale: Locale,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  if (
    condition.op === 'empty' ||
    condition.op === 'not_empty' ||
    ['this_month', 'last_month', 'this_quarter', 'this_year'].includes(condition.op)
  )
    return ''
  if (condition.op === 'last_n_days') return t('lastDays', { count: condition.value })
  if (Array.isArray(condition.value)) {
    const labels = condition.value.map((value) => {
      const choice = field?.choices?.find((item) => item.value === value)
      if (choice) return t(`choices.${field?.key}.${value}`, { defaultValue: choice.label })
      return field?.type === 'record' ? t('unknownRecord') : value
    })
    return labels.length > 1 ? `${labels[0]}, +${labels.length - 1}` : (labels[0] ?? '')
  }
  if (typeof condition.value !== 'string') return ''
  if (field?.type === 'money') return formatMoney(condition.value, currency, locale)
  if (field?.type === 'choice') {
    const choice = field.choices?.find((item) => item.value === condition.value)
    return choice
      ? t(`choices.${field.key}.${condition.value}`, { defaultValue: choice.label })
      : condition.value
  }
  return condition.value
}
